import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Gift, Trophy, ArrowLeft, CheckCircle, Copy, Phone, Sparkles } from "lucide-react";
import { Link } from "wouter";
import { trackEvent } from "@/lib/mixpanel";
import { capturePostHogEvent } from "@/lib/posthog";

interface PrizeEligibility {
  testsCompleted: number;
  eligible: boolean;
  milestone?: number;
  prizeAmount?: number;
  milestones?: number[];
}

interface CampaignWin {
  id: string;
  campaignId: string;
  prizeAmount: number;
  status: string;
  drawnAt: string;
  campaign: {
    id: string;
    name: string;
    description?: string;
    defaultMerchantId?: string;
    defaultMerchantName?: string;
  };
}

interface AdminGrant {
  id: string;
  prizeAmount: number;
  merchantName: string;
  merchantId: string;
  status: string;
  deliveredToPhone: string | null;
  giftmeCardCode: string | null;
  giftmeCardPin: string | null;
  issuedAt: string | null;
  awardedAt: string;
}

interface CurrentUser {
  phoneNumber?: string;
  firstName?: string;
}

const GIFTME_RED = "#DC2626";

function GiftmeLogo({ className = "h-8" }: { className?: string }) {
  return <img src="/giftme-logo.webp" alt="Giftme" className={className} />;
}

function GiftCardVisual({ amount, merchantName, claimed }: { amount: number; merchantName?: string; claimed?: boolean }) {
  return (
    <div className="relative overflow-hidden rounded-2xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${GIFTME_RED}, #991B1B)` }}>
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-12 translate-x-12" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-8 -translate-x-8" />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <GiftmeLogo className="h-7 brightness-0 invert" />
          {claimed && (
            <span className="flex items-center gap-1 text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full">
              <CheckCircle className="w-3.5 h-3.5" /> Claimed
            </span>
          )}
        </div>
        <p className="text-3xl font-bold mb-1">JMD ${(amount / 100).toFixed(2)}</p>
        <p className="text-sm text-white/80">{merchantName || "Smart Card"}</p>
      </div>
    </div>
  );
}

export default function ClaimPrize() {
  const { toast } = useToast();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [claimingType, setClaimingType] = useState<'milestone' | 'campaign' | null>(null);
  const [selectedCampaignWin, setSelectedCampaignWin] = useState<CampaignWin | null>(null);
  const [claimingGrantId, setClaimingGrantId] = useState<string | null>(null);
  const [grantPhones, setGrantPhones] = useState<Record<string, string>>({});
  const [claimedGrants, setClaimedGrants] = useState<Record<string, { cardCode?: string; cardPin?: string; phone: string }>>({});

  useEffect(() => {
    apiRequest('POST', '/api/notifications/mark-prize-read').then(() => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    }).catch(() => {});
  }, []);

  const { data: currentUser } = useQuery<CurrentUser>({
    queryKey: ['/api/auth/me'],
    select: (data: any) => data?.user,
  });

  const { data: prizeData, isLoading: prizeLoading } = useQuery<PrizeEligibility>({
    queryKey: ['/api/prizes/check'],
  });

  const { data: campaignWins = [], isLoading: campaignLoading } = useQuery<CampaignWin[]>({
    queryKey: ['/api/prizes/campaign-wins'],
    retry: 1
  });

  const { data: adminGrants = [], isLoading: adminGrantsLoading } = useQuery<AdminGrant[]>({
    queryKey: ['/api/prizes/admin-grants'],
    retry: 1
  });

  const claimPrizeMutation = useMutation({
    mutationFn: async (data: { milestone: number; prizeAmount: number; phone: string }) => {
      const response = await apiRequest('POST', '/api/prizes/claim', data);
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Prize Claimed!",
          description: `Your JMD $${(data.prize.prizeAmount / 100).toFixed(2)} smart card has been sent to your phone!`,
        });
        trackEvent('Prize Claimed', {
          prizeAmount: data.prize.prizeAmount,
          merchantId: data.prize.merchantId,
          merchantName: data.prize.merchantName,
          milestone: data.prize.milestone,
        });
        capturePostHogEvent('Prize Claimed', {
          prize_amount: data.prize.prizeAmount,
          merchant_name: data.prize.merchantName,
          milestone: data.prize.milestone,
        });
        setPhoneNumber("");
        setClaimingType(null);
        queryClient.invalidateQueries({ queryKey: ['/api/prizes/check'] });
        queryClient.invalidateQueries({ queryKey: ['/api/prizes/history'] });
        queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
        queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to claim prize",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    },
  });

  const claimCampaignPrizeMutation = useMutation({
    mutationFn: async (data: { campaignResultId: string; phone: string }) => {
      const response = await apiRequest('POST', '/api/prizes/claim-campaign', data);
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Campaign Prize Claimed!",
          description: `Your JMD $${(data.campaignWin.prizeAmount / 100).toFixed(2)} smart card has been sent to your phone!`,
        });
        setPhoneNumber("");
        setClaimingType(null);
        setSelectedCampaignWin(null);
        queryClient.invalidateQueries({ queryKey: ['/api/prizes/campaign-wins'] });
        queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
        queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to claim campaign prize",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    },
  });

  const claimAdminGrantMutation = useMutation({
    mutationFn: async (data: { rewardId: string; phone: string }) => {
      const response = await apiRequest('POST', '/api/prizes/claim-admin-grant', data);
      return response.json();
    },
    onSuccess: (data, variables) => {
      setClaimedGrants(prev => ({
        ...prev,
        [variables.rewardId]: {
          cardCode: data.cardCode,
          cardPin: data.cardPin,
          phone: data.deliveredToPhone,
        }
      }));
      setClaimingGrantId(null);
      toast({
        title: "Smart Card Sent!",
        description: `Your smart card has been sent to ${data.deliveredToPhone}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/prizes/admin-grants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to claim prize",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    },
  });

  const handleClaimMilestonePrize = () => {
    if (!phoneNumber || !prizeData?.milestone || !prizeData?.prizeAmount) {
      toast({ title: "Invalid data", description: "Please enter a valid phone number", variant: "destructive" });
      return;
    }
    claimPrizeMutation.mutate({ milestone: prizeData.milestone, prizeAmount: prizeData.prizeAmount, phone: phoneNumber });
  };

  const handleClaimCampaignPrize = (campaignWin: CampaignWin) => {
    if (!phoneNumber) {
      toast({ title: "Invalid data", description: "Please enter a valid phone number", variant: "destructive" });
      return;
    }
    claimCampaignPrizeMutation.mutate({ campaignResultId: campaignWin.id, phone: phoneNumber });
  };

  const handleClaimAdminGrant = (grant: AdminGrant) => {
    const phone = grantPhones[grant.id] || "";
    if (!phone) {
      toast({ title: "Phone required", description: "Enter a phone number to receive your smart card", variant: "destructive" });
      return;
    }
    setClaimingGrantId(grant.id);
    claimAdminGrantMutation.mutate({ rewardId: grant.id, phone });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({ title: `${label} copied!` });
    });
  };

  const isLoading = prizeLoading || campaignLoading || adminGrantsLoading;
  const hasPrizes = prizeData?.eligible || campaignWins.length > 0 || adminGrants.length > 0;
  const isClaiming = claimPrizeMutation.isPending || claimCampaignPrizeMutation.isPending;

  // Pre-fill grant phone numbers from user profile (only once when grants load)
  useEffect(() => {
    if (adminGrantsLoading || adminGrants.length === 0) return;
    const phone = currentUser?.phoneNumber || "";
    setGrantPhones(prev => {
      const next = { ...prev };
      let changed = false;
      adminGrants.forEach(g => {
        if (!(g.id in next)) {
          next[g.id] = phone;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [adminGrants, adminGrantsLoading, currentUser?.phoneNumber]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white dark:from-red-950/20 dark:to-background">
        <div className="container max-w-2xl mx-auto py-8 px-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-3">
              <GiftmeLogo className="h-10 mx-auto" />
              <p className="text-muted-foreground">Loading your prizes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white dark:from-red-950/20 dark:to-background">
      <div className="container max-w-2xl mx-auto py-6 px-4">
        {/* Back button */}
        <div className="mb-6">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" data-testid="button-back">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-4">
            <GiftmeLogo className="h-12" />
          </div>
          <h1 className="text-3xl font-bold mb-2" data-testid="title-claim-prize">
            {hasPrizes ? "You have prizes!" : "Your Prizes"}
          </h1>
          <p className="text-muted-foreground">
            {hasPrizes
              ? "Claim your smart cards below. Delivered instantly by Giftme."
              : "Earn smart cards by completing tests on EduFocal Academy."}
          </p>

          {/* Milestone progress bar: always visible */}
          {(() => {
            const completed = prizeData?.testsCompleted || 0;
            const milestones = prizeData?.milestones;
            if (!milestones || milestones.length === 0) return null;
            const nextMilestone = milestones.find(m => completed < m);
            if (!nextMilestone) return null; // All milestones completed
            const prevMilestone = milestones[milestones.indexOf(nextMilestone) - 1] || 0;
            const progress = Math.min(100, ((completed - prevMilestone) / (nextMilestone - prevMilestone)) * 100);
            const remaining = nextMilestone - completed;

            return (
              <div className="max-w-xs mx-auto space-y-2 mt-6">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{completed} tests completed</span>
                  <span>{remaining} more to go</span>
                </div>
                <div className="h-3 bg-red-100 dark:bg-red-950/40 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${progress}%`, backgroundColor: GIFTME_RED }}
                  />
                </div>
                <p className="text-xs font-medium" style={{ color: GIFTME_RED }}>
                  Next Giftme smart card at {nextMilestone} tests
                </p>
              </div>
            );
          })()}
        </div>

        {!hasPrizes ? (
          /* Empty state */
          <Card className="border-dashed border-2 border-red-200 dark:border-red-900/40">
            <CardContent className="py-10">
              <div className="text-center space-y-5">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-2" style={{ backgroundColor: `${GIFTME_RED}10` }}>
                  <Gift className="h-10 w-10" style={{ color: GIFTME_RED }} />
                </div>
                <div>
                  <p className="text-lg font-semibold mb-1">No prizes yet</p>
                  <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                    Complete tests to earn Giftme smart cards!
                  </p>
                </div>
                <div className="pt-2">
                  <Link href="/learning">
                    <Button style={{ backgroundColor: GIFTME_RED }} className="hover:opacity-90 text-white" data-testid="button-start-learning">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Start Learning
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">

            {/* Admin-Granted Prizes */}
            {adminGrants.map((grant) => {
              const justClaimed = claimedGrants[grant.id];
              const isIssued = grant.status === 'issued' || justClaimed;
              const isPendingClaim = grant.status === 'pending_claim' && !justClaimed;
              const cardCode = justClaimed?.cardCode || grant.giftmeCardCode;
              const cardPin = justClaimed?.cardPin || grant.giftmeCardPin;
              const deliveredPhone = justClaimed?.phone || grant.deliveredToPhone;

              return (
                <Card
                  key={grant.id}
                  className="overflow-hidden border-0 shadow-lg"
                  data-testid={`card-admin-grant-${grant.id}`}
                >
                  <CardContent className="p-0">
                    <GiftCardVisual
                      amount={grant.prizeAmount}
                      merchantName={grant.merchantName}
                      claimed={isIssued}
                    />

                    <div className="p-5 space-y-4">
                      {isIssued ? (
                        <div className="space-y-3">
                          {deliveredPhone && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Phone className="w-4 h-4" />
                              Delivered to {deliveredPhone}
                            </div>
                          )}
                          {cardCode && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Card Code</p>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-sm font-mono bg-muted border rounded-lg px-3 py-2.5 break-all">
                                  {cardCode}
                                </code>
                                <Button size="icon" variant="outline" onClick={() => copyToClipboard(cardCode, "Card code")} className="shrink-0">
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                          {cardPin && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Card PIN</p>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-sm font-mono bg-muted border rounded-lg px-3 py-2.5">
                                  {cardPin}
                                </code>
                                <Button size="icon" variant="outline" onClick={() => copyToClipboard(cardPin, "Card PIN")} className="shrink-0">
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : isPendingClaim ? (
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            Enter the phone number where Giftme should deliver this card.
                          </p>
                          <div className="space-y-1.5">
                            <Label htmlFor={`grant-phone-${grant.id}`}>
                              Phone Number
                            </Label>
                            <Input
                              id={`grant-phone-${grant.id}`}
                              type="tel"
                              placeholder="876XXXXXXX"
                              value={grantPhones[grant.id] || ""}
                              onChange={(e) => setGrantPhones(prev => ({ ...prev, [grant.id]: e.target.value }))}
                            />
                          </div>
                          <Button
                            className="w-full text-white hover:opacity-90"
                            size="lg"
                            style={{ backgroundColor: GIFTME_RED }}
                            onClick={() => handleClaimAdminGrant(grant)}
                            disabled={claimAdminGrantMutation.isPending && claimingGrantId === grant.id}
                          >
                            {claimAdminGrantMutation.isPending && claimingGrantId === grant.id ? (
                              "Sending..."
                            ) : (
                              <><Phone className="w-4 h-4 mr-2" />Send to My Phone</>
                            )}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Milestone Prize */}
            {prizeData?.eligible && (
              <Card className="overflow-hidden border-0 shadow-lg" data-testid="card-milestone-prize">
                <CardContent className="p-0">
                  <GiftCardVisual
                    amount={prizeData.prizeAmount || 0}
                    merchantName={`${prizeData.milestone}+ Tests Milestone`}
                  />

                  <div className="p-5 space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Tests completed</span>
                      <Badge variant="secondary" data-testid="badge-tests-completed" className="font-bold">
                        {prizeData.testsCompleted}
                      </Badge>
                    </div>

                    {claimingType === 'milestone' ? (
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="milestone-phone">Phone Number</Label>
                          <Input
                            id="milestone-phone"
                            type="tel"
                            placeholder="876XXXXXXX"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            data-testid="input-milestone-phone"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => { setClaimingType(null); setPhoneNumber(""); }} className="flex-1" disabled={isClaiming} data-testid="button-cancel-milestone">
                            Cancel
                          </Button>
                          <Button onClick={handleClaimMilestonePrize} disabled={isClaiming || !phoneNumber} className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: GIFTME_RED }} data-testid="button-confirm-milestone">
                            {isClaiming ? "Processing..." : "Claim Prize"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button className="w-full text-white hover:opacity-90" size="lg" style={{ backgroundColor: GIFTME_RED }} onClick={() => setClaimingType('milestone')} data-testid="button-claim-milestone-prize">
                        <Gift className="w-4 h-4 mr-2" />
                        Claim Your Prize
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Campaign Prizes */}
            {campaignWins.map((win) => (
              <Card key={win.id} className="overflow-hidden border-0 shadow-lg" data-testid={`card-campaign-prize-${win.id}`}>
                <CardContent className="p-0">
                  <GiftCardVisual
                    amount={win.prizeAmount}
                    merchantName={win.campaign.defaultMerchantName || win.campaign.name}
                  />

                  <div className="p-5 space-y-4">
                    <div>
                      <p className="font-semibold">{win.campaign.name}</p>
                      {win.campaign.description && (
                        <p className="text-sm text-muted-foreground mt-1">{win.campaign.description}</p>
                      )}
                    </div>

                    {claimingType === 'campaign' && selectedCampaignWin?.id === win.id ? (
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor={`campaign-phone-${win.id}`}>Phone Number</Label>
                          <Input
                            id={`campaign-phone-${win.id}`}
                            type="tel"
                            placeholder="876XXXXXXX"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            data-testid={`input-campaign-phone-${win.id}`}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => { setClaimingType(null); setSelectedCampaignWin(null); setPhoneNumber(""); }} className="flex-1" disabled={isClaiming} data-testid={`button-cancel-campaign-${win.id}`}>
                            Cancel
                          </Button>
                          <Button onClick={() => handleClaimCampaignPrize(win)} disabled={isClaiming || !phoneNumber} className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: GIFTME_RED }} data-testid={`button-confirm-campaign-${win.id}`}>
                            {isClaiming ? "Processing..." : "Claim Prize"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button className="w-full text-white hover:opacity-90" size="lg" style={{ backgroundColor: GIFTME_RED }} onClick={() => { setClaimingType('campaign'); setSelectedCampaignWin(win); }} data-testid={`button-claim-campaign-${win.id}`}>
                        <Gift className="w-4 h-4 mr-2" />
                        Claim Campaign Prize
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 mt-10 pb-6">
          <span className="text-sm text-muted-foreground">Prizes delivered by</span>
          <GiftmeLogo className="h-6" />
        </div>
      </div>
    </div>
  );
}
